import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Backdrop, Grow, Modal } from '@material-ui/core';
import Select from 'react-select';
import 'react-select/dist/react-select.css';
import * as Utils from '../../commons/utils/Utils.js';
import { ICONO_CONFIRMAR, ICONO_CANCELAR } from '../../commons/dialog/Dialog.js';

/**
 * VEC-3318 — despachar una línea pendiente de la grilla de Solicitudes.
 *
 * DOS FORMAS DE RESOLVER, y la diferencia no es cosmética:
 *
 *  - **Transferencia**: los repuestos ya existen en otro depósito y se traen. El stock no se crea,
 *    se mueve. Por eso hay que elegir de dónde, y solo se ofrecen los depósitos que tienen
 *    suficiente — la lista la calcula el backend, que es el único que sabe qué hay disponible.
 *  - **Compra**: los repuestos no existían y entran al sistema. No hay origen que elegir.
 *
 * LA CANTIDAD MANDA SOBRE LOS ORÍGENES. El despacho puede ser parcial, así que qué depósitos
 * alcanzan depende de cuánto se vaya a entregar: pedir 2 puede tener tres orígenes posibles y pedir
 * 10 ninguno. Por eso la lista se vuelve a pedir cada vez que cambia la cantidad, y no una sola vez
 * al abrir.
 *
 * El modal NO decide si el despacho es válido: eso lo hace el backend, que es quien puede mirar el
 * stock en el mismo instante en que escribe. Acá se evita el error obvio (no ofrecer un depósito que
 * no alcanza) y se muestra el que igual llegue.
 *
 * ESTRUCTURA BOOTSTRAP, NO `DialogTitle`/`DialogContent`/`DialogActions`.
 *
 * Esos tres traen sus PROPIOS paddings (`MuiDialogTitle-root`, `MuiDialogContent-root`,
 * `MuiDialogActions-root`) y se aplican ENCIMA de `modal-header` / `modal-body` / `modal-footer`,
 * así que el modal quedaba con márgenes distintos a los ~95 modales Bootstrap del sistema. Es lo que
 * advierte docs/design-system/dialogos-y-modales.md ("Evitar Dialog / Modal de Material-UI para
 * modales nuevos") y lo mismo que hizo descartar la primera versión de GridHistoryModal.
 *
 * `Modal` de MUI queda SOLO como contenedor —backdrop, portal y foco—, igual que en
 * TicketsGridExport. La caja la arma Bootstrap, y por eso el modal se alinea arriba y mide lo que
 * miden los demás.
 */

const BASE_URL = '/inventario/solicitudes/detalles';

/**
 * Tope de la observación. Es el `maxLength` del input Y el denominador del contador, así que el
 * número vive en UN solo lugar: mostrar "/ 255" y dejar escribir 300 es peor que no mostrar nada.
 * Coincide con el `varchar(255)` de `item_stock_movimientos.motivo`.
 */
const MAX_MOTIVO = 255;

const MODO_TRANSFERENCIA = 'transferencia';
const MODO_COMPRA = 'compra';

const InventarioSolicitudDespachoModal = (props) => {
  const intl = useIntl();

  const detalle = props.detalle;

  const [modo, setModo] = useState(MODO_TRANSFERENCIA);
  const [cantidad, setCantidad] = useState('');
  const [origen, setOrigen] = useState('');
  const [motivo, setMotivo] = useState('');

  const [origenes, setOrigenes] = useState([]);
  const [cargandoOrigenes, setCargandoOrigenes] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const pendiente = detalle ? Number(detalle.cantidadPendiente) : 0;

  // Al abrir con otra línea se reinicia todo: si quedara el estado anterior, se podría despachar la
  // línea nueva con la cantidad o el origen de la anterior.
  useEffect(() => {
    if (!detalle) return;
    setModo(MODO_TRANSFERENCIA);
    setCantidad(String(pendiente));
    setOrigen('');
    setMotivo('');
    setOrigenes([]);
  }, [detalle && detalle.id]);

  /**
   * SIN TOPE SUPERIOR: se puede despachar MÁS de lo pendiente.
   *
   * Los repuestos entran al DEPÓSITO, no al ticket: que lleguen 5 contra un pedido de 3 es real
   * —una caja cerrada, un lote que conviene traer entero— y el sobrante queda como stock del
   * almacén, que es donde tiene que estar. La línea se cierra igual y el pendiente no baja de 0.
   */
  /**
   * Los orígenes en la forma que espera react-select.
   *
   * El disponible va DENTRO del label y no en una columna aparte: el control no soporta contenido
   * estructurado sin un `optionRenderer` propio, y el número es justamente lo que decide de dónde
   * conviene traerlo.
   */
  const opcionesDeOrigen = () =>
    origenes.map((o) => ({ value: String(o.id), label: o.nombre + ' (' + o.disponible + ')', type: o.type }));

  const cantidadValida = () => {
    const n = Number(cantidad);
    return Number.isInteger(n) && n >= 1;
  };

  /**
   * Los orígenes posibles para la cantidad vigente.
   *
   * Depende de `cantidad` a propósito (ver el encabezado). Solo se pide en modo transferencia: en
   * compra no hay origen que elegir y sería una llamada al pedo.
   */
  useEffect(() => {
    if (!detalle || modo !== MODO_TRANSFERENCIA) return undefined;

    const n = Number(cantidad);
    if (!cantidadValida()) {
      // Cantidad inválida: no se piden orígenes (el backend devolvería 400) y se limpia la lista,
      // para que no quede ofreciendo depósitos calculados para otra cantidad.
      setOrigenes([]);
      return undefined;
    }

    // Guarda de secuencia: al tipear la cantidad salen varios pedidos y sin esto se pinta el que
    // responda último, no el último que se pidió.
    let vigente = true;
    setCargandoOrigenes(true);

    Utils.getData(BASE_URL + '/' + detalle.id + '/origenes?cantidad=' + n)
      .then((data) => {
        if (!vigente) return;
        const lista = (data && data.origenes) || [];
        setOrigenes(lista);
        // Si el origen elegido dejó de alcanzar para la cantidad nueva, se descarta: mandarlo igual
        // sería un 400 seguro.
        setOrigen((prev) => (lista.some((o) => String(o.id) === String(prev)) ? prev : ''));
      })
      .catch(() => {
        if (vigente) setOrigenes([]);
      })
      .finally(() => {
        if (vigente) setCargandoOrigenes(false);
      });

    return () => {
      vigente = false;
    };
  }, [detalle && detalle.id, modo, cantidad, pendiente]);

  const puedeGuardar = () => {
    if (guardando || !cantidadValida()) return false;
    // En transferencia hace falta el origen; en compra no hay nada más que elegir.
    return modo === MODO_COMPRA || origen !== '';
  };

  const handleGuardar = () => {
    if (!puedeGuardar()) return;

    const cuerpo = { modo: modo, cantidad: Number(cantidad) };
    if (modo === MODO_TRANSFERENCIA) {
      const elegido = origenes.find((o) => String(o.id) === String(origen));
      cuerpo.origenId = Number(origen);
      // El `type` sale SIEMPRE de la opción elegida. Ya no cae a 'almacen': con tres tipos en el
      // catálogo (almacén, base, taller) adivinar el tipo manda el par equivocado y el backend
      // transferiría desde una ubicación que no es. Sin opción, el par viaja incompleto y el
      // backend responde 400, que es exactamente lo que hay que ver si esto llegara a pasar.
      if (elegido) cuerpo.origenType = elegido.type;
    }
    if (motivo.trim() !== '') cuerpo.motivo = motivo.trim();

    setGuardando(true);
    Utils.sendData(BASE_URL + '/' + detalle.id + '/despachar', 'POST', cuerpo)
      .then((data) => props.onDespachado(data))
      // `Utils.sendData` ya muestra el error por Swal —con el mensaje interpolado del backend—, así
      // que acá solo se corta la propagación y se reabre el botón.
      .catch(() => {})
      .finally(() => setGuardando(false));
  };

  if (!detalle) return null;

  const item = detalle.item || {};

  return (
    <Modal open={!!detalle} onClose={props.onClose} closeAfterTransition BackdropComponent={Backdrop} BackdropProps={{ timeout: 300 }}>
      <Grow in={!!detalle}>
        <div tabIndex="-1" role="dialog">
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header bg-fleet">
                <h4 className="modal-title text-white mb-0">
                  <i className="fa fa-truck align-middle icon-modal-title"></i>
                  <FormattedMessage id="inventarioSolicitudes.despacho.titulo" defaultMessage=" Despachar repuesto" />
                </h4>
                {/* La × de cerrar: va en TODOS los modales del sistema y es la salida
                    que la gente busca primero. */}
                <button type="button" className="close" aria-label="Close" onClick={props.onClose}>
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>

              <div className="modal-body modal-grey modal-mh ovf-x-hidden">
                {/* Qué se está despachando y cuánto queda. Sin esto el modal es un formulario sin sujeto. */}
                <div className="mb-3">
                  <div style={{ fontWeight: 600 }}>{item.numeroDeParte ? item.numeroDeParte + ' · ' : ''}{item.descripcion}</div>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    <FormattedMessage
                      id="inventarioSolicitudes.despacho.contexto"
                      defaultMessage="Ticket {ticket} · Depósito {deposito} · Pendiente: {pendiente}"
                      values={{
                        ticket: detalle.ticket ? detalle.ticket.id : '-',
                        deposito: detalle.ubicacion ? detalle.ubicacion.nombre : '-',
                        pendiente: <b>{pendiente}</b>,
                      }}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>
                    <FormattedMessage id="inventarioSolicitudes.despacho.modo" defaultMessage="¿Cómo se resuelve?" />
                  </label>
                  <div>
                    <label className="mr-3" style={{ cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="modoDespacho"
                        className="mr-1"
                        checked={modo === MODO_TRANSFERENCIA}
                        onChange={() => setModo(MODO_TRANSFERENCIA)}
                      />
                      <FormattedMessage
                        id="inventarioSolicitudes.despacho.modo_transferencia"
                        defaultMessage="Transferir desde otro depósito"
                      />
                    </label>
                    <label style={{ cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="modoDespacho"
                        className="mr-1"
                        checked={modo === MODO_COMPRA}
                        onChange={() => setModo(MODO_COMPRA)}
                      />
                      <FormattedMessage id="inventarioSolicitudes.despacho.modo_compra" defaultMessage="Comprar" />
                    </label>
                  </div>
                  <small className="text-muted">
                    {modo === MODO_TRANSFERENCIA ? (
                      <FormattedMessage
                        id="inventarioSolicitudes.despacho.ayuda_transferencia"
                        defaultMessage="Los repuestos salen del depósito que elijas y entran al de la solicitud."
                      />
                    ) : (
                      <FormattedMessage
                        id="inventarioSolicitudes.despacho.ayuda_compra"
                        defaultMessage="Los repuestos entran como stock nuevo. No se descuenta de ningún depósito."
                      />
                    )}
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="despachoCantidad">
                    <FormattedMessage id="inventarioSolicitudes.despacho.cantidad" defaultMessage="Cantidad a despachar" />
                  </label>
                  {/* Sin `max`: el atributo bloquearía las flechas del input y marcaría el campo como
                      inválido al escribir de más, que es justamente lo que tiene que poder hacerse. */}
                  <input
                    id="despachoCantidad"
                    type="number"
                    className={'form-control' + (cantidad !== '' && !cantidadValida() ? ' is-invalid' : '')}
                    min={1}
                    value={cantidad}
                    onChange={(e) => setCantidad(e.target.value)}
                  />
                  {cantidad !== '' && !cantidadValida() && (
                    <div className="invalid-feedback d-block">
                      <FormattedMessage
                        id="inventarioSolicitudes.despacho.cantidad_invalida_v2"
                        defaultMessage="Tiene que ser un número entero de 1 o más."
                      />
                    </div>
                  )}
                  {/* Aviso, NO error: entregar de más está permitido, pero es lo bastante inusual como para
                      que convenga decirlo antes de confirmar —el sobrante queda en el depósito—. */}
                  {cantidadValida() && Number(cantidad) > pendiente && (
                    <small className="text-warning">
                      <FormattedMessage
                        id="inventarioSolicitudes.despacho.mas_que_pendiente"
                        defaultMessage="Estás entregando {exceso} más de lo pedido. El sobrante queda como stock del depósito."
                        values={{ exceso: <b>{Number(cantidad) - pendiente}</b> }}
                      />
                    </small>
                  )}
                </div>

                {modo === MODO_TRANSFERENCIA && (
                  <div className="form-group">
                    <label htmlFor="despachoOrigen">
                      <FormattedMessage id="inventarioSolicitudes.despacho.origen" defaultMessage="Depósito de origen" />
                    </label>
                    {/* `react-select` y no un `<select>` nativo: es el control que usa el resto del sistema
                        cuando el desplegable tiene que verse como el formulario que lo rodea (el ABM de
                        ingreso de VEC-3314 ya lo usa para los ítems). El nativo hereda el estilo del
                        navegador y desentona con el resto del modal.
                        `valueKey`/`labelKey` son de la v1, que es la instalada. */}
                    <Select
                      id="despachoOrigen"
                      name="despachoOrigen"
                      options={opcionesDeOrigen()}
                      valueKey="value"
                      labelKey="label"
                      value={origen}
                      onChange={(opcion) => setOrigen(opcion ? opcion.value : '')}
                      isLoading={cargandoOrigenes}
                      disabled={cargandoOrigenes || !cantidadValida()}
                      // Sin la × de limpiar: el origen es obligatorio en transferencia, y poder vaciarlo solo
                      // agrega una forma de dejar el formulario inválido.
                      clearable={false}
                      placeholder={
                        cargandoOrigenes
                          ? intl.formatMessage({ id: 'inventarioSolicitudes.despacho.buscando', defaultMessage: 'Buscando depósitos…' })
                          : intl.formatMessage({ id: 'inventarioSolicitudes.despacho.elegir_origen', defaultMessage: 'Elegí un depósito' })
                      }
                      noResultsText={intl.formatMessage({
                        id: 'inventarioSolicitudes.despacho.sin_coincidencias',
                        defaultMessage: 'Sin coincidencias',
                      })}
                    />
                    {/* El vacío informativo importa: sin esto, un select sin opciones parece un error de la
                        pantalla y no la respuesta correcta a "no hay de dónde traerlo". */}
                    {!cargandoOrigenes && cantidadValida() && origenes.length === 0 && (
                      <small className="text-danger">
                        <FormattedMessage
                          id="inventarioSolicitudes.despacho.sin_origenes"
                          defaultMessage="Ningún depósito tiene {cantidad} unidades disponibles. Probá con menos cantidad o resolvelo por compra."
                          values={{ cantidad: Number(cantidad) }}
                        />
                      </small>
                    )}
                  </div>
                )}

                <div className="form-group mb-0">
                  <label htmlFor="despachoMotivo">
                    <FormattedMessage id="inventarioSolicitudes.despacho.motivo" defaultMessage="Observación (opcional)" />
                  </label>
                  <textarea
                    id="despachoMotivo"
                    className="form-control"
                    rows={3}
                    maxLength={MAX_MOTIVO}
                    value={motivo}
                    onChange={(e) => setMotivo(e.target.value)}
                  />
                  {/* Cuánto queda por escribir. `maxLength` ya frena en seco, pero sin contador el
                      usuario descubre el tope recién cuando el campo deja de responder —y ahí no
                      sabe si se rompió algo—. */}
                  <small className="text-muted float-right">
                    {motivo.length} / {MAX_MOTIVO}
                  </small>
                </div>
              </div>

      {/* Confirmar A LA IZQUIERDA y cancelar a la derecha, con los iconos y colores de
          docs/design-system/dialogos-y-modales.md ("Confirmaciones (dos botones)"). Es el orden que
          siguen 63 de las 68 confirmaciones del sistema; estaba al revés. */}
              <div className="modal-footer modal-grey">
                <button type="button" className="btn btn-primary" onClick={handleGuardar} disabled={!puedeGuardar()}>
                  <i className={ICONO_CONFIRMAR + ' mr-1'} />
                  <FormattedMessage id="inventarioSolicitudes.despacho.confirmar" defaultMessage="Confirmar despacho" />
                </button>
                <button type="button" className="btn btn-danger" onClick={props.onClose} disabled={guardando}>
                  <i className={ICONO_CANCELAR + ' mr-1'} />
                  <FormattedMessage id="inventarioSolicitudes.despacho.cancelar" defaultMessage="Cancelar" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </Grow>
    </Modal>
  );
};

export default InventarioSolicitudDespachoModal;
